/**
 * A film's sound: its effects and a light score, synthesized rather than recorded.
 *
 * Nothing here is a sample. Every tap of a hammer, every page turn and every note of the
 * marimba is built from oscillators, filters and a block of noise, so the soundtrack
 * costs no download and carries no license — the film can be posted anywhere without a
 * stranger's recording in it — and it is as deterministic as the picture: the noise is
 * seeded, never random, so the same film renders the same samples every time.
 *
 * A film's `score` schedules its cues on a `Mix`, in the film's own seconds, and the
 * whole soundtrack is rendered at once in an `OfflineAudioContext` — faster than real
 * time, and the same buffer whether it is played under the page's canvas or recorded
 * into an ad. Effects and music ride separate buses, so a recording can leave the music
 * out for whoever wants to lay their own under it.
 *
 * Levels are set for taste, not for impact: the effects are foley under the picture, the
 * score sits under the effects, a compressor glues them and the result is normalized a
 * decibel under full scale.
 */

import type { Film, Site } from "./film.js";

/** What a soundtrack has in it. */
export interface SoundOptions {
  effects: boolean;
  music: boolean;
}

export const FULL_MIX: SoundOptions = { effects: true, music: true };

/** Past the film's end, so the last chord rings out: the recorder holds the end card this long. */
export const TAIL = 0.5;

const SAMPLE_RATE = 48_000;

/** A seeded generator in [0, 1) (mulberry32): noise that is the same on every render. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function midi(note: number): number {
  return 440 * 2 ** ((note - 69) / 12);
}

interface Envelope {
  attack?: number;
  peak: number;
  decay: number;
}

interface Placement {
  gain?: number;
  pan?: number;
}

/**
 * The mixing desk a score plays into: two buses, a shared room, and the voices.
 * Every voice takes its start in the film's seconds.
 */
export class Mix {
  private readonly effectsBus: GainNode;
  private readonly musicBus: GainNode;
  private readonly noiseBuffer: AudioBuffer;
  private readonly panners = new Map<string, AudioNode>();
  /**
   * Voices waiting to be built, by when they sound. A node is part of the graph from the
   * moment it is made, sounding or not, and a renderer visits every one of them every
   * block: thirty seconds of voices built up front took most of ten seconds to render.
   * So they are built just ahead of their time (`flush`, from `renderSoundtrack`).
   */
  private readonly queue: { at: number; make: () => void }[] = [];
  private sorted = true;
  private seq = 0;

  constructor(
    readonly ctx: BaseAudioContext,
    readonly options: SoundOptions,
  ) {
    // Glue, then a limiter to catch what the glue lets through: a level that holds up on
    // a phone's speaker without anything audibly squashed.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -11;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.08;
    limiter.connect(ctx.destination);
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 10;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.16;
    compressor.connect(limiter);

    const room = ctx.createConvolver();
    room.buffer = impulse(ctx);
    room.connect(compressor);

    this.effectsBus = ctx.createGain();
    this.effectsBus.gain.value = 0.9;
    this.effectsBus.connect(compressor);
    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 0.2;
    this.musicBus.connect(compressor);
    for (const [bus, send] of [
      [this.effectsBus, 0.12],
      [this.musicBus, 0.35],
    ] as const) {
      // The room hears in mono: half the convolution, and nobody can tell.
      const wet = ctx.createGain();
      wet.channelCount = 1;
      wet.channelCountMode = "explicit";
      wet.gain.value = send;
      bus.connect(wet);
      wet.connect(room);
    }

    const random = seeded(0x5eed);
    this.noiseBuffer = ctx.createBuffer(1, Math.round(ctx.sampleRate * 2), ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = random() * 2 - 1;
    }
  }

  // ---- plumbing ----------------------------------------------------------------------

  /** Builds every voice that sounds before `until`, in order. */
  flush(until: number): void {
    if (!this.sorted) {
      this.queue.sort((a, b) => a.at - b.at);
      this.sorted = true;
    }
    while (this.queue.length > 0 && this.queue[0]!.at < until) {
      this.queue.shift()!.make();
    }
  }

  private later(at: number, make: () => void): void {
    if (this.queue.length > 0 && at < this.queue[this.queue.length - 1]!.at) {
      this.sorted = false;
    }
    this.queue.push({ at, make });
  }

  /** A bus at a place in the stereo field; one panner per place, shared by every voice there. */
  private out(bus: AudioNode, pan = 0): AudioNode {
    const place = Math.round(Math.max(-1, Math.min(1, pan)) * 20) / 20;
    if (place === 0) {
      return bus;
    }
    const key = `${bus === this.musicBus ? "music" : "effects"}:${place}`;
    let panner = this.panners.get(key);
    if (panner === undefined) {
      const created = this.ctx.createStereoPanner();
      created.pan.value = place;
      created.connect(bus);
      this.panners.set(key, created);
      panner = created;
    }
    return panner;
  }

  /** A gain stage shaped by an envelope: up in `attack`, then down to nothing over `decay`. */
  private shaped(at: number, env: Envelope, into: AudioNode): GainNode {
    const gain = this.ctx.createGain();
    const attack = env.attack ?? 0.003;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(env.peak, at + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + env.decay);
    gain.connect(into);
    return gain;
  }

  private tone(
    at: number,
    into: AudioNode,
    shape: OscillatorType,
    frequency: number,
    env: Envelope,
    glide?: { to: number; over: number },
  ): void {
    this.later(at, () => this.toneNow(at, into, shape, frequency, env, glide));
  }

  private toneNow(
    at: number,
    into: AudioNode,
    shape: OscillatorType,
    frequency: number,
    env: Envelope,
    glide?: { to: number; over: number },
  ): void {
    const osc = this.ctx.createOscillator();
    osc.type = shape;
    osc.frequency.setValueAtTime(frequency, at);
    if (glide !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, glide.to), at + glide.over);
    }
    osc.connect(this.shaped(at, env, into));
    osc.start(at);
    osc.stop(at + (env.attack ?? 0.003) + env.decay + 0.05);
  }

  private noise(
    at: number,
    into: AudioNode,
    filter: { type: BiquadFilterType; frequency: number; q?: number; to?: number; over?: number },
    env: Envelope,
  ): void {
    // A different stretch of the noise for every sound, chosen by order, not by chance.
    const offset = ((this.seq++ * 0.6180339887) % 1) * 1.8;
    this.later(at, () => this.noiseNow(at, into, filter, env, offset));
  }

  private noiseNow(
    at: number,
    into: AudioNode,
    filter: { type: BiquadFilterType; frequency: number; q?: number; to?: number; over?: number },
    env: Envelope,
    offset: number,
  ): void {
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    const biquad = this.ctx.createBiquadFilter();
    biquad.type = filter.type;
    biquad.frequency.setValueAtTime(filter.frequency, at);
    if (filter.to !== undefined) {
      biquad.frequency.exponentialRampToValueAtTime(filter.to, at + (filter.over ?? env.decay));
    }
    biquad.Q.value = filter.q ?? 0.8;
    source.connect(biquad);
    biquad.connect(this.shaped(at, env, into));
    source.start(at, offset);
    source.stop(at + (env.attack ?? 0.003) + env.decay + 0.05);
  }

  private effect(pan = 0): AudioNode | undefined {
    return this.options.effects ? this.out(this.effectsBus, pan) : undefined;
  }

  private music(pan = 0): AudioNode | undefined {
    return this.options.music ? this.out(this.musicBus, pan) : undefined;
  }

  // ---- effects -------------------------------------------------------------------------

  /** A hammer on a nail in wood: a bright knock with a short body under it. */
  tap(at: number, { gain = 1, pan = 0, pitch = 1 }: Placement & { pitch?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "triangle", 1250 * pitch, { peak: 0.5 * gain, decay: 0.06 }, { to: 760 * pitch, over: 0.04 });
    this.tone(at, into, "sine", 210 * pitch, { peak: 0.45 * gain, decay: 0.09 });
    this.noise(at, into, { type: "bandpass", frequency: 2800 * pitch, q: 1.4 }, { peak: 0.35 * gain, decay: 0.025 });
  }

  /** Something heavy set down: a low drop and a puff of air. */
  thud(at: number, { gain = 1, pan = 0, weight = 1 }: Placement & { weight?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "sine", 95 / weight, { peak: 0.8 * gain, decay: 0.22 }, { to: 48 / weight, over: 0.12 });
    this.noise(at, into, { type: "lowpass", frequency: 520 / weight, q: 0.7 }, { peak: 0.5 * gain, decay: 0.08 });
  }

  /** A sheet laid flat on framing: a flat crack with a little weight behind it. */
  slap(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "bandpass", frequency: 1500, q: 0.7 }, { attack: 0.002, peak: 0.6 * gain, decay: 0.09 });
    this.tone(at, into, "sine", 120, { peak: 0.35 * gain, decay: 0.12 }, { to: 70, over: 0.1 });
  }

  /** Metal: a few partials that do not belong to one note, decaying at their own rates. */
  clank(at: number, { gain = 1, pan = 0, base = 520 }: Placement & { base?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    [
      [1, 0.4, 0.35],
      [2.37, 0.25, 0.22],
      [4.13, 0.14, 0.12],
    ].forEach(([ratio = 1, peak = 0, decay = 0]) => this.tone(at, into, "sine", base * ratio, { peak: peak * gain, decay }));
    this.noise(at, into, { type: "highpass", frequency: 3000 }, { peak: 0.2 * gain, decay: 0.02 });
  }

  /** Rubber bounding off the floor. */
  boing(at: number, { gain = 1, pan = 0, pitch = 1 }: Placement & { pitch?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.later(at, () => {
      const osc = this.ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(170 * pitch, at);
      osc.frequency.exponentialRampToValueAtTime(290 * pitch, at + 0.04);
      osc.frequency.exponentialRampToValueAtTime(130 * pitch, at + 0.22);
      osc.connect(this.shaped(at, { peak: 0.5 * gain, decay: 0.24 }, into));
      osc.start(at);
      osc.stop(at + 0.3);
    });
  }

  /** Air moving: noise through a band that sweeps, swelling and falling away. */
  whoosh(
    at: number,
    duration: number,
    { gain = 1, pan = 0, from = 400, to = 2600 }: Placement & { from?: number; to?: number } = {},
  ): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(
      at,
      into,
      { type: "bandpass", frequency: from, q: 1.1, to, over: duration },
      { attack: duration * 0.55, peak: 0.42 * gain, decay: duration * 0.45 },
    );
  }

  /** A mouse button. */
  click(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "square", 2300, { attack: 0.001, peak: 0.12 * gain, decay: 0.012 });
    this.noise(at, into, { type: "highpass", frequency: 4500 }, { attack: 0.001, peak: 0.25 * gain, decay: 0.01 });
  }

  /** A small wooden tick: a stud set in place on the deck. */
  tick(at: number, { gain = 1, pan = 0, pitch = 1 }: Placement & { pitch?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "sine", 1650 * pitch, { attack: 0.001, peak: 0.22 * gain, decay: 0.03 });
    this.tone(at, into, "sine", 420 * pitch, { attack: 0.001, peak: 0.18 * gain, decay: 0.04 });
  }

  /** A bubble appearing. */
  pop(at: number, { gain = 1, pan = 0, pitch = 1 }: Placement & { pitch?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "sine", 320 * pitch, { attack: 0.004, peak: 0.4 * gain, decay: 0.07 }, { to: 880 * pitch, over: 0.05 });
  }

  /** Sheets of paper in the air: a scatter of tiny bright rustles. */
  flutter(at: number, duration: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    const random = seeded(Math.round(at * 1000) + 7);
    for (let t = 0; t < duration; t += 0.025 + random() * 0.035) {
      this.noise(
        at + t,
        into,
        { type: "bandpass", frequency: 3200 + random() * 2500, q: 1.2 },
        { attack: 0.004, peak: (0.12 + random() * 0.12) * gain, decay: 0.03 },
      );
    }
  }

  /** A pen ticking a box. */
  scribble(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "bandpass", frequency: 3600, q: 2.5, to: 2400, over: 0.05 }, { attack: 0.005, peak: 0.3 * gain, decay: 0.05 });
    this.noise(at + 0.07, into, { type: "bandpass", frequency: 2600, q: 2.5, to: 4200, over: 0.08 }, { attack: 0.008, peak: 0.34 * gain, decay: 0.08 });
  }

  /** A struck bell: a fundamental and the two partials that make a bell a bell. */
  bell(at: number, frequency: number, { gain = 1, pan = 0, decay = 1.1 }: Placement & { decay?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "sine", frequency, { attack: 0.002, peak: 0.28 * gain, decay });
    this.tone(at, into, "sine", frequency * 2.76, { attack: 0.002, peak: 0.1 * gain, decay: decay * 0.5 });
    this.tone(at, into, "sine", frequency * 5.4, { attack: 0.002, peak: 0.05 * gain, decay: decay * 0.25 });
  }

  /** The garage door: a ratchet that speeds up as the door rolls, over a rumble. */
  rattle(at: number, duration: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "lowpass", frequency: 260, q: 0.6 }, { attack: duration * 0.4, peak: 0.45 * gain, decay: duration * 0.6 });
    let t = 0;
    let i = 0;
    while (t < duration) {
      this.tone(at + t, into, "triangle", 900 + (i % 3) * 140, { attack: 0.001, peak: 0.08 * gain, decay: 0.02 });
      t += 0.07 - 0.045 * (t / duration);
      i += 1;
    }
  }

  /** A printer feeding pages. */
  whirr(at: number, duration: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.later(at, () => this.whirrNow(at, duration, gain, into));
  }

  private whirrNow(at: number, duration: number, gain: number, into: AudioNode): void {
    const osc = this.ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = 118;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 700;
    const level = this.ctx.createGain();
    level.gain.setValueAtTime(0, at);
    level.gain.linearRampToValueAtTime(0.09 * gain, at + 0.06);
    level.gain.setValueAtTime(0.09 * gain, at + duration - 0.08);
    level.gain.linearRampToValueAtTime(0, at + duration);
    osc.connect(filter);
    filter.connect(level);
    level.connect(into);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  /** Wheels on a floor or a path: a low grumble with the odd bump in it. */
  roll(at: number, duration: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "lowpass", frequency: 240, q: 1.2 }, { attack: 0.12, peak: 0.32 * gain, decay: duration });
    for (let t = 0.18; t < duration; t += 0.31) {
      this.tone(at + t, into, "sine", 150, { attack: 0.002, peak: 0.12 * gain, decay: 0.05 });
    }
  }

  /** A small car's engine and its tires on the road, revving away at `away`. */
  engine(at: number, duration: number, away: number, { gain = 1 }: Placement = {}): void {
    const into = this.effect(0);
    if (into === undefined) {
      return;
    }
    this.later(at, () => this.engineNow(at, duration, away, gain, into));
    this.noise(at, into, { type: "lowpass", frequency: 700, q: 0.5 }, { attack: 0.3, peak: 0.035 * gain, decay: duration });
  }

  private engineNow(at: number, duration: number, away: number, gain: number, into: AudioNode): void {
    const level = this.ctx.createGain();
    level.gain.setValueAtTime(0, at);
    level.gain.linearRampToValueAtTime(0.05 * gain, at + 0.25);
    level.gain.setValueAtTime(0.05 * gain, at + away);
    level.gain.linearRampToValueAtTime(0.0001, at + duration);
    const pan = this.ctx.createStereoPanner();
    pan.pan.setValueAtTime(-0.1, at);
    pan.pan.setValueAtTime(-0.1, at + away);
    pan.pan.linearRampToValueAtTime(0.9, at + duration);
    level.connect(pan);
    pan.connect(into);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(380, at);
    filter.frequency.setValueAtTime(380, at + away);
    filter.frequency.linearRampToValueAtTime(900, at + duration);
    filter.connect(level);
    for (const [shape, base] of [
      ["sawtooth", 52],
      ["triangle", 104],
    ] as const) {
      const osc = this.ctx.createOscillator();
      osc.type = shape;
      osc.frequency.setValueAtTime(base, at);
      osc.frequency.setValueAtTime(base, at + away);
      osc.frequency.exponentialRampToValueAtTime(base * 1.7, at + duration);
      osc.connect(filter);
      osc.start(at);
      osc.stop(at + duration + 0.05);
    }
  }

  /** A songbird: two quick rising whistles. */
  chirp(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    for (const [offset, from, to] of [
      [0, 2900, 4300],
      [0.11, 3100, 4700],
    ] as const) {
      this.tone(at + offset, into, "sine", from, { attack: 0.006, peak: 0.12 * gain, decay: 0.07 }, { to, over: 0.05 });
    }
  }

  /**
   * One stroke of a hand saw, `length` seconds long: a rasp through a narrow band that rises
   * on the push and falls on the pull, with the teeth catching through it.
   */
  saw(at: number, push: boolean, length: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    const [from, to] = push ? [1700, 3100] : [2700, 1500];
    this.noise(at, into, { type: "bandpass", frequency: from, q: 3.2, to, over: length }, { attack: length * 0.35, peak: 0.34 * gain, decay: length * 0.65 });
    for (let t = 0.01; t < length - 0.01; t += 0.016) {
      this.noise(at + t, into, { type: "highpass", frequency: 3800 }, { attack: 0.001, peak: 0.07 * gain, decay: 0.008 });
    }
  }

  /** A dog's sneeze: a quick sniff in, and a sharp little burst out. */
  sneeze(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "bandpass", frequency: 700, q: 2, to: 1900, over: 0.12 }, { attack: 0.1, peak: 0.16 * gain, decay: 0.04 });
    this.noise(at + 0.15, into, { type: "bandpass", frequency: 2400, q: 1.1, to: 800, over: 0.08 }, { attack: 0.002, peak: 0.6 * gain, decay: 0.09 });
    this.tone(at + 0.15, into, "triangle", 980, { attack: 0.002, peak: 0.12 * gain, decay: 0.07 }, { to: 520, over: 0.07 });
  }

  /** Wet concrete sliding off a barrow: a low slurry under it, and a glop now and then. */
  pour(at: number, duration: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "lowpass", frequency: 420, q: 1.6 }, { attack: 0.15, peak: 0.34 * gain, decay: duration });
    const random = seeded(Math.round(at * 1000) + 11);
    for (let t = 0.08; t < duration - 0.1; t += 0.13 + random() * 0.14) {
      const size = 0.7 + random() * 0.6;
      this.tone(at + t, into, "sine", 150 * size, { attack: 0.004, peak: 0.22 * gain, decay: 0.09 }, { to: 70 * size, over: 0.08 });
      this.noise(at + t, into, { type: "lowpass", frequency: 900, q: 1.4, to: 300, over: 0.06 }, { attack: 0.003, peak: 0.18 * gain, decay: 0.06 });
    }
  }

  /**
   * Something dragged flat across a surface for `duration`: a straight board over wet
   * concrete low down, sandpaper over wood when `bright` is high.
   */
  scrape(at: number, duration: number, { gain = 1, pan = 0, bright = 1 }: Placement & { bright?: number } = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(
      at,
      into,
      { type: "bandpass", frequency: 900 * bright, q: 1.3, to: 1150 * bright, over: duration },
      { attack: duration * 0.3, peak: 0.3 * gain, decay: duration * 0.7 },
    );
  }

  /** A paw pressed into something soft and wet. */
  squelch(at: number, { gain = 1, pan = 0 }: Placement = {}): void {
    const into = this.effect(pan);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "lowpass", frequency: 1300, q: 2.4, to: 320, over: 0.07 }, { attack: 0.004, peak: 0.3 * gain, decay: 0.07 });
    this.tone(at, into, "sine", 300, { attack: 0.003, peak: 0.1 * gain, decay: 0.06 }, { to: 150, over: 0.05 });
  }

  // ---- music -----------------------------------------------------------------------------

  /** A marimba bar: the fundamental, its bright fourth harmonic that dies fast, and a knock. */
  mallet(at: number, note: number, velocity = 1, { pan = 0, ring = 1 }: { pan?: number; ring?: number } = {}): void {
    const into = this.music(pan);
    if (into === undefined) {
      return;
    }
    const f = midi(note);
    const decay = Math.min(1.4, (0.25 + 90 / f) * ring);
    this.tone(at, into, "sine", f, { attack: 0.003, peak: 0.5 * velocity, decay });
    this.tone(at, into, "sine", f * 4, { attack: 0.002, peak: 0.12 * velocity, decay: Math.min(0.12, decay * 0.3) });
    if (f * 10 < SAMPLE_RATE / 2.4) {
      this.tone(at, into, "sine", f * 10, { attack: 0.001, peak: 0.03 * velocity, decay: 0.03 });
    }
  }

  /** A glockenspiel, for the moments that sparkle. */
  glock(at: number, note: number, velocity = 1, pan = 0): void {
    const into = this.music(pan);
    if (into === undefined) {
      return;
    }
    const f = midi(note);
    this.tone(at, into, "sine", f, { attack: 0.002, peak: 0.32 * velocity, decay: 0.9 });
    this.tone(at, into, "sine", f * 2.76, { attack: 0.002, peak: 0.09 * velocity, decay: 0.35 });
  }

  /** A shaker: a breath of high noise, for the off-beats. */
  shaker(at: number, velocity = 1): void {
    const into = this.music(0.15);
    if (into === undefined) {
      return;
    }
    this.noise(at, into, { type: "bandpass", frequency: 7600, q: 1 }, { attack: 0.012, peak: 0.16 * velocity, decay: 0.05 });
  }

  /** A soft kick: a low thump with no click on it. */
  kick(at: number, velocity = 1): void {
    const into = this.music(0);
    if (into === undefined) {
      return;
    }
    this.tone(at, into, "sine", 120, { attack: 0.002, peak: 0.85 * velocity, decay: 0.26 }, { to: 46, over: 0.11 });
  }
}

/** A room: two seconds of seeded noise falling away, a little darker as it goes. */
function impulse(ctx: BaseAudioContext): AudioBuffer {
  const seconds = 1.8;
  const length = Math.round(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const random = seeded(0xa11 + channel);
    const data = buffer.getChannelData(channel);
    let low = 0;
    for (let i = 0; i < length; i += 1) {
      const t = i / ctx.sampleRate;
      const raw = (random() * 2 - 1) * Math.exp(-t * 3.2);
      // A one-pole low-pass that closes as the tail goes on: walls soak up the highs.
      const k = 0.55 + 0.4 * Math.min(1, t / seconds);
      low = low * k + raw * (1 - k);
      data[i] = t < 0.012 ? 0 : low * 1.6;
    }
  }
  return buffer;
}

/** The film's soundtrack, rendered whole; `undefined` for a film with no score. */
export async function renderSoundtrack(film: Film, site: Site, options: SoundOptions = FULL_MIX): Promise<AudioBuffer | undefined> {
  if (film.score === undefined || (!options.effects && !options.music)) {
    return undefined;
  }
  const length = Math.ceil((film.duration + TAIL) * SAMPLE_RATE);
  const ctx = new OfflineAudioContext(2, length, SAMPLE_RATE);
  const mix = new Mix(ctx, options);
  film.score(mix, site);
  // Build the voices a window at a time, a little ahead of the render: a third of a second
  // of blocks, so every suspension falls on a block's edge.
  const window = (128 * 125) / SAMPLE_RATE;
  const ahead = 0.05;
  mix.flush(window + ahead);
  for (let at = window; at < length / SAMPLE_RATE; at += window) {
    void ctx.suspend(at).then(() => {
      mix.flush(at + window + ahead);
      void ctx.resume();
    });
  }
  const buffer = await ctx.startRendering();
  // A decibel and a half under full scale: room for the true peak an encoder finds between
  // samples, so an AAC of it does not clip.
  normalize(buffer, 0.84);
  return buffer;
}

/** Scales a buffer so its loudest sample is `peak`: every render leaves at the same level. */
function normalize(buffer: AudioBuffer, peak: number): void {
  let loudest = 0;
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    for (const sample of buffer.getChannelData(channel)) {
      loudest = Math.max(loudest, Math.abs(sample));
    }
  }
  if (loudest === 0) {
    return;
  }
  const scale = peak / loudest;
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = data[i]! * scale;
    }
  }
}

/** A soundtrack as a 16-bit stereo WAV file: for an editor that lays it under other footage. */
export function encodeWav(buffer: AudioBuffer): Blob {
  const channels = buffer.numberOfChannels;
  const frames = buffer.length;
  const bytes = 44 + frames * channels * 2;
  const view = new DataView(new ArrayBuffer(bytes));
  const text = (offset: number, value: string): void => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };
  text(0, "RIFF");
  view.setUint32(4, bytes - 8, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, frames * channels * 2, true);
  const data = Array.from({ length: channels }, (_, channel) => buffer.getChannelData(channel));
  let offset = 44;
  for (let i = 0; i < frames; i += 1) {
    for (let channel = 0; channel < channels; channel += 1) {
      const sample = Math.max(-1, Math.min(1, data[channel]![i]!));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([view.buffer], { type: "audio/wav" });
}
